import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import CryptoJS from 'crypto-js';
import logo from '../../assets/logo.webp';

// DEBUG flag - Set to true to enable console logs, false to disable
const DEBUG = false;

// Add a custom logger function
const logger = {
  error: (...args) => {
    if (import.meta.env.DEV) {
      console.error(...args);
    }
  },
  log: (...args) => {
    if (DEBUG) {
      console.log(...args);
    }
  }
};

const DataContext = createContext();
const ENCRYPTION_KEY = import.meta.env.VITE_APP_KEY || '';
if (DEBUG) {
  console.log('[DataContext] ENCRYPTION_KEY', ENCRYPTION_KEY);
  console.log('[DataContext] ENCRYPTION_KEY validation', {
    hasKey: !!ENCRYPTION_KEY,
    keyLength: ENCRYPTION_KEY.length,
    expectedBase64Length: 44, // 32 bytes * 4/3 = ~43, rounded up with padding
    isValidBase64: /^[A-Za-z0-9+/]*={0,2}$/.test(ENCRYPTION_KEY)
  });
}
export const DataProvider = ({ children }) => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedLang, setLastFetchedLang] = useState(null);
  const fetchingRef = useRef(false);

  // Log initialization
  if (DEBUG) {
    console.log('[DataContext] Component initialized', {
      language,
      hasData: !!data,
      isLoading,
      hasError: !!error,
      encryptionKeyConfigured: !!ENCRYPTION_KEY
    });
  }

  const decryptData = (encryptedData) => {
    if (DEBUG) console.log('[DataContext] Step 1: Starting decryption process');
    try {
      // Validate input
      if (DEBUG) console.log('[DataContext] Step 2: Validating encrypted data', {
        hasData: !!encryptedData,
        dataType: typeof encryptedData,
        dataLength: encryptedData?.length
      });
      
      if (!encryptedData || typeof encryptedData !== 'string') {
        throw new Error('Invalid encrypted data format');
      }

      // Check if encryption key is available
      if (DEBUG) console.log('[DataContext] Step 3: Checking encryption key', {
        hasKey: !!ENCRYPTION_KEY,
        keyLength: ENCRYPTION_KEY?.length
      });
      
      if (!ENCRYPTION_KEY) {
        throw new Error('Encryption key is not configured');
      }

      // Decode base64 to binary
      if (DEBUG) console.log('[DataContext] Step 4: Decoding base64 to binary');
      const rawData = atob(encryptedData);
      if (DEBUG) console.log('[DataContext] Step 5: Base64 decoded', {
        rawDataLength: rawData.length
      });
      
      // Validate minimum length (must have IV + some data)
      if (rawData.length < 17) {
        throw new Error('Encrypted data is too short');
      }
      
      // Convert to Uint8Array
      if (DEBUG) console.log('[DataContext] Step 6: Converting to Uint8Array');
      const bytes = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; i++) {
        bytes[i] = rawData.charCodeAt(i);
      }

      // Extract IV (first 16 bytes) and ciphertext
      if (DEBUG) console.log('[DataContext] Step 7: Extracting IV and ciphertext');
      const iv = bytes.slice(0, 16);
      const ciphertext = bytes.slice(16);
      if (DEBUG) console.log('[DataContext] Step 8: IV and ciphertext extracted', {
        ivLength: iv.length,
        ciphertextLength: ciphertext.length
      });

      // Convert to CryptoJS format
      if (DEBUG) console.log('[DataContext] Step 9: Converting to CryptoJS format');
      const ivWordArray = CryptoJS.lib.WordArray.create(iv);
      const ciphertextWordArray = CryptoJS.lib.WordArray.create(ciphertext);

      // Parse encryption key
      if (DEBUG) console.log('[DataContext] Step 10: Parsing encryption key', {
        keyBase64: ENCRYPTION_KEY,
        keyLength: ENCRYPTION_KEY.length
      });
      
      // Clean the key - remove Laravel's "base64:" prefix if present
      let cleanedKey = ENCRYPTION_KEY.trim();
      if (cleanedKey.startsWith('base64:')) {
        cleanedKey = cleanedKey.substring(7); // Remove "base64:" prefix (7 characters)
        if (DEBUG) console.log('[DataContext] Step 10.0: Removed base64: prefix', {
          originalLength: ENCRYPTION_KEY.length,
          cleanedLength: cleanedKey.length
        });
      }
      
      // Decode base64 key to get raw bytes (same as backend's base64_decode)
      // Then convert to WordArray for CryptoJS
      let keyBytes;
      try {
        const keyBase64Decoded = atob(cleanedKey);
        keyBytes = new Uint8Array(keyBase64Decoded.length);
        for (let i = 0; i < keyBase64Decoded.length; i++) {
          keyBytes[i] = keyBase64Decoded.charCodeAt(i);
        }
        if (DEBUG) console.log('[DataContext] Step 10.1: Key decoded from base64', {
          keyBytesLength: keyBytes.length,
          expectedLength: 32
        });
        
        if (keyBytes.length !== 32) {
          throw new Error(`Key length is ${keyBytes.length}, expected 32 bytes for AES-256`);
        }
      } catch (keyError) {
        console.error('[DataContext] Key decoding error:', keyError);
        throw new Error(`Failed to decode encryption key: ${keyError.message}`);
      }
      
      // Convert to CryptoJS WordArray
      const key = CryptoJS.lib.WordArray.create(keyBytes);
      if (DEBUG) console.log('[DataContext] Step 10.2: Key converted to WordArray', {
        keyWordArrayLength: key.sigBytes,
        keyWords: key.words?.length
      });

      // Decrypt
      if (DEBUG) console.log('[DataContext] Step 11: Decrypting data', {
        ciphertextLength: ciphertextWordArray.sigBytes,
        ivLength: ivWordArray.sigBytes,
        keyLength: key.sigBytes
      });
      
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: ciphertextWordArray },
        key,
        { 
          iv: ivWordArray,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        }
      );
      
      if (DEBUG) console.log('[DataContext] Step 11.1: Decryption result', {
        decryptedSigBytes: decrypted.sigBytes,
        hasDecrypted: decrypted.sigBytes > 0,
        decryptedWords: decrypted.words?.length
      });

      // Convert to UTF-8 and parse JSON
      if (DEBUG) console.log('[DataContext] Step 12: Converting decrypted data to UTF-8');
      
      // Extract raw bytes from WordArray
      const words = decrypted.words;
      const sigBytes = decrypted.sigBytes;
      const rawBytes = new Uint8Array(sigBytes);
      
      // Convert WordArray words to bytes
      for (let i = 0; i < sigBytes; i++) {
        const wordIndex = Math.floor(i / 4);
        const byteIndex = i % 4;
        const word = words[wordIndex];
        rawBytes[i] = (word >>> (24 - (byteIndex * 8))) & 0xff;
      }
      
      if (DEBUG) console.log('[DataContext] Step 12.1: Extracted raw bytes', {
        byteLength: rawBytes.length,
        firstBytes: Array.from(rawBytes.slice(0, 20))
      });
      
      // Try to decode as UTF-8 using TextDecoder
      let decryptedText;
      try {
        const decoder = new TextDecoder('utf-8', { fatal: true });
        decryptedText = decoder.decode(rawBytes);
        if (DEBUG) console.log('[DataContext] Step 12.2: UTF-8 decoding successful using TextDecoder');
      } catch (utf8Error) {
        console.error('[DataContext] Step 12.2: UTF-8 decoding failed', utf8Error);
        // If UTF-8 fails, the key is likely wrong - show first few bytes as hex for debugging
        const hexPreview = Array.from(rawBytes.slice(0, 50))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(' ');
        console.error('[DataContext] Step 12.3: First 50 bytes as hex:', hexPreview);
        throw new Error('Failed to decode decrypted data as UTF-8. The encryption key may be incorrect. Please verify VITE_APP_KEY matches between frontend and backend.');
      }
      
      if (DEBUG) console.log('[DataContext] Step 13: Decrypted text obtained', {
        hasText: !!decryptedText,
        textLength: decryptedText?.length,
        textPreview: decryptedText?.substring(0, 100)
      });
      
      if (!decryptedText) {
        throw new Error('Decryption resulted in empty data');
      }

      if (DEBUG) console.log('[DataContext] Step 14: Parsing JSON from decrypted text');
      const parsedData = JSON.parse(decryptedText);
      if (DEBUG) console.log('[DataContext] Step 15: Decryption successful!', {
        hasData: !!parsedData,
        dataKeys: parsedData ? Object.keys(parsedData) : []
      });
      
      return parsedData;
    } catch (error) {
      console.error('[DataContext] Decryption error at step:', error);
      logger.error('Decryption error:', error);
      throw new Error(`Failed to decrypt data: ${error.message}`);
    }
  };

  const fetchData = useCallback(async (lang) => {
    if (DEBUG) console.log('[DataContext] fetchData called', {
      lang,
      isFetching: fetchingRef.current,
      lastFetchedLang,
      hasData: !!data
    });

    // Prevent duplicate requests
    if (fetchingRef.current || (lastFetchedLang === lang && data)) {
      if (DEBUG) console.log('[DataContext] Skipping fetch - duplicate request prevented');
      return;
    }

    try {
      if (DEBUG) console.log('[DataContext] Starting fetch process');
      fetchingRef.current = true;
      setError(null);
      setIsLoading(true);

      const url = `/api/homepage?lang=${lang}`;
      if (DEBUG) console.log('[DataContext] Making API request', { url });
      const response = await fetch(url);
      if (DEBUG) console.log('[DataContext] API response received', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      // Check if response is ok
      if (!response.ok) {
        if (DEBUG) console.log('[DataContext] Response not OK, reading error text');
        const errorText = await response.text();
        if (DEBUG) console.log('[DataContext] Error text received', { errorText });
        let errorMessage = `Failed to fetch data (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          if (DEBUG) console.log('[DataContext] Parsed error JSON', { errorJson });
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch {
          // If not JSON, use the text or default message
          if (DEBUG) console.log('[DataContext] Error text is not JSON, using raw text');
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Parse JSON response
      if (DEBUG) console.log('[DataContext] Parsing JSON response');
      let result;
      try {
        result = await response.json();
        if (DEBUG) console.log('[DataContext] JSON parsed successfully', {
          resultType: typeof result,
          resultLength: result?.length,
          resultPreview: typeof result === 'string' ? result.substring(0, 100) + '...' : result
        });
      } catch (jsonError) {
        console.error('[DataContext] JSON parsing error:', jsonError);
        logger.error('JSON parsing error:', jsonError);
        throw new Error('Invalid response format from server');
      }

      // Validate result
      if (DEBUG) console.log('[DataContext] Validating result', {
        hasResult: !!result,
        resultType: typeof result,
        isString: typeof result === 'string'
      });
      
      if (!result || typeof result !== 'string') {
        console.error('[DataContext] Invalid result format', { result });
        throw new Error('Invalid data format received from server');
      }

      // Decrypt and set data
      if (DEBUG) console.log('[DataContext] Starting decryption process');
      const decryptedData = decryptData(result);
      if (DEBUG) console.log('[DataContext] Decryption completed, setting data', {
        hasDecryptedData: !!decryptedData,
        dataKeys: decryptedData ? Object.keys(decryptedData) : []
      });
      
      setData(decryptedData);
      setLastFetchedLang(lang); // Update last fetched language
      if (DEBUG) console.log('[DataContext] Data set successfully', { lang });
    } catch (error) {
      console.error('[DataContext] Error in fetchData:', error);
      logger.error('Error fetching data:', error);
      setError(error.message || 'An unexpected error occurred');
    } finally {
      if (DEBUG) console.log('[DataContext] Fetch process completed, cleaning up');
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [lastFetchedLang, data]);

  // Fetch data whenever language changes
  useEffect(() => {
    if (DEBUG) console.log('[DataContext] useEffect triggered', {
      language,
      hasData: !!data,
      lastFetchedLang,
      shouldFetch: language && (!data || lastFetchedLang !== language)
    });

    if (language && (!data || lastFetchedLang !== language)) {
      if (DEBUG) console.log('[DataContext] Scheduling fetchData with setTimeout');
      // Use setTimeout to ensure we're not in a render cycle
      const timeoutId = setTimeout(() => {
        if (DEBUG) console.log('[DataContext] setTimeout callback executed, calling fetchData');
        fetchData(language);
      }, 0);
      
      return () => {
        if (DEBUG) console.log('[DataContext] Cleaning up setTimeout');
        clearTimeout(timeoutId);
      };
    } else {
      if (DEBUG) console.log('[DataContext] Skipping fetch - conditions not met');
    }
  }, [language, fetchData, lastFetchedLang, data]);

  if (DEBUG) console.log('[DataContext] Render check', {
    isLoading,
    hasError: !!error,
    hasData: !!data,
    language
  });

  if (isLoading) {
    if (DEBUG) console.log('[DataContext] Rendering loading screen');
    return (
      <div className="loading-screen">
        <div className="loader"></div>
      </div>
    );
  }

  if (error) {
    if (DEBUG) console.log('[DataContext] Rendering error screen', { error });
    return (
      <div className="flex fixed inset-0 justify-center items-center bg-gray-100">
        <div className="p-8 mx-4 w-full max-w-md bg-white rounded-lg shadow-xl">
          <div className="text-center">
            <div className="mb-6">
              <img src={logo} alt="Logo" className="mx-auto mb-4 h-16" />
              <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-800">Oops! Something went wrong</h2>
              <p className="mb-6 text-gray-600">{error || 'We encountered a technical error. Please try again.'}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors duration-200 hover:bg-blue-700"
            >
              <svg className="mr-2 w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (DEBUG) console.log('[DataContext] Rendering DataProvider with context', {
    hasData: !!data,
    isLoading,
    hasError: !!error
  });

  return (
    <DataContext.Provider value={{
      data,
      isLoading,
      error,
      refetch: fetchData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};